import React, { useEffect, useRef, useState } from "react";
import BaseDialog from "../modelDialogs/BaseDialog";
import Page from "../models/Page";
import Floor from "../models/Floor";
import RoomDialog from "../modelDialogs/RoomDialog";
import Logger from "../Logger";
import { showToast } from "./Toasts";
import Browser from "../browser";
import BackgroundImagePosition from "../models/BackgroundImagePosition";

const logger = new Logger();

/**
 * The `ShowRoomSubmittalsBackground` function in TypeScript React handles selecting and transforming
 * images for use as background images in a room, including options for clipboard and upload
 * functionality.
 * @returns The `ShowRoomSubmittalsBackground` function is returning a JSX element representing a
 * dialog component. The dialog allows users to select a drawing as a background image for a room. It
 * includes a list of background images with options for uploading images, selecting images from the
 * clipboard, and choosing from a predefined list of demo room backgrounds. The dialog also provides
 * functionality to set the selected image as the background image
 */
export default function ShowRoomSubmittalsBackground(props: {
  page: Page;
  onChange?: () => void;
  fromRoomCreation?: { parent: Floor; tag: string; name: string; cabinetId: string };
}) {
  const { page, onChange, fromRoomCreation } = props;
  const [selectedImageName, setSelectedImageName] = useState("");
  const [selectedImageURL, setSelectedImageURL] = useState("");
  const [showClipboardRow, setShowClipboardRow] = useState(true);
  const emptyImage =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQgAAAEGCAYAAACZ/AuPAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAMTSURBVHhe7dQxAYBAEMCwA/9azwIsbE8dJEsd9NrdZwB+3F8BDgYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggDDzAjBpBdXtsdrzAAAAAElFTkSuQmCC"; // no itx
  const uploadImageUrl = useRef(emptyImage);
  const clipboardImageUrl = useRef(emptyImage);
  const isApple = Browser.getBrowser().includes("Safari");
  const isFirefox = Browser.getBrowser().includes("Firefox");

  useEffect(() => {
    transformSelectedImageToURL();
  }, [selectedImageName]);

  /**
   * The function `transformSelectedImageToURL` transforms a selected image to a URL, handling different
   * cases such as clipboard, upload, and converting an image element to a canvas.
   * @returns The function `transformSelectedImageToURL` returns the selected image URL after
   * transforming the image element to a canvas and converting the canvas to an image element.
   */
  async function transformSelectedImageToURL() {
    if (!selectedImageName.includes(".")) {
      if (selectedImageName == "clipboard") {
        setSelectedImageURL(clipboardImageUrl.current);
        return;
      } else if (selectedImageName == "fromUpload") {
        setSelectedImageURL(uploadImageUrl.current);
        return;
      }
    }
    // because of rerendering we have to wait to set picture
    setTimeout(() => {
      const image = document.getElementById(selectedImageName) as HTMLImageElement;

      if (image) {
        // transform img element to canvas an convert the cavas to an image element
        const toDataURL = () => {
          const canvas = document.createElement("canvas");

          canvas.width = image.naturalWidth; // real immage width
          canvas.height = image.naturalHeight; // real immage heght
          const canvas2D = canvas.getContext("2d");
          if (canvas2D) {
            canvas2D.drawImage(image, 0, 0);

            const dataURL = canvas.toDataURL();
            return dataURL;
          }
        };
        const imageUrl = toDataURL();
        if (imageUrl) {
          page.resizeImageAfterLoad(imageUrl).then((result) => {
            setSelectedImageURL(result as string);
          });
        }
      }
    }, 1000);
  }

  /**
   * The function `onChangeBackgroundImgUrl` sets a new background image for a drawing area and updates
   * its position and properties accordingly.
   * @param {string} backgroundImgUrl - The `backgroundImgUrl` parameter is a string that represents
   * the URL of the background image that will be displayed in the main drawing area of the page. The
   * function `onChangeBackgroundImgUrl` is responsible for handling the change of the background image
   * URL and updating the background image position accordingly.
   */
  function onChangeBackgroundImgUrl(backgroundImgUrl: string) {
    async function getImagevirtualElement(url: string) {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    }
    const mainDrawingArea = document.getElementById("mainDrawingArea");
    let imageHeight = 0;
    let imageWidth = 0;

    let areaHeight = 21 * 33;
    let areaWidth = 29.7 * 33;
    if (mainDrawingArea) {
      areaHeight = mainDrawingArea.offsetHeight;
      areaWidth = mainDrawingArea.offsetWidth;
    }
    page.resizeImageAfterLoad(backgroundImgUrl).then((result) => {
      getImagevirtualElement(result as string).then((img) => {
        imageWidth = img.naturalWidth;
        imageHeight = img.naturalHeight;
        const newPosY = (areaHeight - imageHeight) / 2;
        const newPosX = (areaWidth - imageWidth) / 2;
        const newBackgroundImagePosition = new BackgroundImagePosition();
        newBackgroundImagePosition.posY = newPosY;
        newBackgroundImagePosition.posX = newPosX;
        newBackgroundImagePosition.scale = state.settings.defaultBackgroundPictureElements.scale;
        newBackgroundImagePosition.fade = state.settings.defaultBackgroundPictureElements.fade;
        if (!fromRoomCreation?.parent) {
        }
        page.backgroundImgUrl = result as string;
        page.backgroundImagePosition = newBackgroundImagePosition;
        page.raiseOnChange("backgroundImgUrl");
        page.raiseOnChange("backgroundImagePosition");
      });
    });
  }

  /* The above code defines a functional component called ListRow in TypeScript for a React
  application. This component takes in props including name, description, imgSrc, and optional
  additionalElement. */
  function ListRow(props: { name: string; description: string; imgSrc: string; additionalElement?: any }) {
    const { name, description, imgSrc, additionalElement } = props;

    const element = (
      <div style={{ cursor: "pointer", position: "relative" }}>
        <img
          className="backgroundPicture"
          src={imgSrc}
          style={{
            ...imageStyle,

            border: "1px solid lightgrey",
            borderRadius: "2px",
            height: "68px",
            backgroundColor: "var(--panel-background)",
            cursor: "pointer",
          }}
          id={name}
          // onEr}ror={() => setFallbackImage()}
          onClick={() => {
            setSelectedImageName(name as string);
          }}
        />
        {additionalElement}
      </div>
    );

    return (
      <tr
        key={`image_${imgSrc}`}
        id={`backgroundPicture_${name}`}
        style={{
          border: name == selectedImageName ? "2px solid orange" : "", // no itx
          borderRadius: "5px",
        }}
      >
        <td
          style={{
            paddingTop: "10px",
            paddingBottom: "10px",
            width: "60px",
            // border:
            //   selectedImage == image ? "2px solid var(--selectedOrange)" : "",
            // borderRadius: "5px",
          }}
        >
          {element}
        </td>
        <td
          id={`backgroundPicture_${name}_click`}
          style={{
            verticalAlign: "middle",
          }}
          onClick={() => {
            setSelectedImageName(name as string);
          }}
        >
          <div style={{ whiteSpace: "normal" }}> {description}</div>
        </td>
      </tr>
    );
  }
  ////////////////// end list row

  /**
   * The `ListRowClipboard` function in TypeScript React handles reading and displaying images from the
   * clipboard, with functions for checking permissions, loading images, and providing descriptions
   * based on clipboard content.
   * @param props - The code you provided is a React component called `ListRowClipboard` that handles
   * displaying an image from the clipboard and related functionality. It includes functions for reading
   * an image from the clipboard as a base64 string, checking permissions to access the clipboard, and
   * loading the image from the clipboard.
   * @returns The `ListRowClipboard` function returns a JSX element representing a list row component.
   * The component includes an image sourced from the clipboard, a description based on the content of
   * the clipboard image, and an additional element that allows the user to reload the image from the
   * clipboard.
   */
  function ListRowClipboard(props: { imgSrc: string }) {
    const [imageOnClipboard, setImageOnClipboard] = useState(props.imgSrc);

    const name = "clipboard"; // no itx

    //Don`t do this because of permission
    // useEffect(() => {
    //   const setClipboardImage = async () => {
    //     const img = await imageFromClipboardAsBase64();
    //     if (img?.substring(0, 10) == "data:image") {
    //       clipboardImageUrl.current = img;
    //       setImageOnClipboard(img);
    //     } else {
    //       // no Picture in Clipboard
    //     }
    //   };
    //   setClipboardImage().catch(logger.error);
    // }, []);

    //for paste from Clipboard
    /**
     * The function `imageFromClipboardAsBase64` reads an image from the clipboard and returns it as a
     * base64 string, handling different image types.
     * @returns The function `imageFromClipboardAsBase64` returns a Promise that resolves to a string
     * representing the base64 data of an image from the clipboard, or `undefined` if no suitable image
     * type is found in the clipboard items. If an error occurs during the process, it logs the error,
     * handles it accordingly, and may display a toast message.
     */
    /**
     * The function `imageFromClipboardAsBase64` in TypeScript React reads an image from the clipboard and
     * returns it as a base64 string, handling different image types and error cases.
     * @returns The `imageFromClipboardAsBase64` function returns a Promise that resolves to a string
     * (base64 data of an image) or undefined. The `getDescription` function returns a string based on
     * whether the `imageOnClipboard` is equal to `emptyImage`.
     */
    async function imageFromClipboardAsBase64(): Promise<string | undefined> {
      try {
        logger.log("onPasteBackground");
        const clipboardItems = await navigator.clipboard.read();
        if (!clipboardItems || clipboardItems.length == 0) {
          return undefined;
        }

        const firstItem = clipboardItems[0];

        for (const type of firstItem.types) {
          if (type == "image/png" || type == "image/jpg" || type == "image/jpeg") {
            const blob = await clipboardItems[0].getType(type);

            const base64data = await readBlobAsync(blob);
            return base64data;
          } else {
            return type;
          }
        }
        return undefined;
      } catch (ex) {
        logger.error(ex);
        if (!isApple) {
          setShowClipboardRow(false);
        }
        showToast({
          text: state.translate("ShowRoomSubmittalsBackground.clipboardNotSupported", "no clipboard supported"),
          isError: true,
        });
        return;
      }
    }
    /**
     * The function `readBlobAsync` reads a Blob object asynchronously and returns a Promise with the
     * data encoded as a base64 string.
     * @param {Blob} blob - A Blob object representing data that you want to read asynchronously.
     * @returns The `readBlobAsync` function returns a Promise that resolves with a string containing
     * the base64 data of the Blob after it has been read asynchronously.
     */
    function readBlobAsync(blob: Blob): Promise<string> {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();

        reader.readAsDataURL(blob);
        reader.onloadend = function () {
          var base64data = reader.result;
          if (typeof base64data == "string") {
            // logger.log(base64data);
            resolve(base64data);
          }
        };

        reader.onerror = reject;
      });
    }

    /**
     * The function `getDescription` returns a translated message based on whether an image is present
     * on the clipboard.
     * @returns The function `getDescription()` returns a string value based on the condition of
     * `imageOnClipboard`. If `imageOnClipboard` is equal to `emptyImage`, it returns the translated
     * message "no picture in clipboard". Otherwise, it returns the translated message "picture from
     * clipboard".
     */
    function getDescription(): string {
      if (imageOnClipboard == emptyImage) {
        return state.translate("ShowRoomSubmittalsBackground.noPictureInClipboard", "no picture in clipboard");
      } else {
        return state.translate("ShowRoomSubmittalsBackground.pictureFromClipboard", "picture from clipboard");
      }
    }
    /**
     * The function `getPermissionAccessClipboard` checks for permission to access the clipboard and
     * performs actions based on the permission state.
     * @returns The `getPermissionAccessClipboard` function does not explicitly return any value. It
     * contains a try-catch block that handles checking for permission access to the clipboard using the
     * `navigator.permissions.query` method. Depending on the result of the permission query, it either
     * calls `loadPictureFromClipvoard`, logs a message using `logger.log`, or shows a toast message
     * using `showToast`. If an
     */
    function getPermissionAccessClipboard() {
      try {
        (navigator.permissions as any)
          .query({ name: "clipboard-read" }) // no itx
          .then((result: any) => {
            if (result.state === "granted") {
              loadPictureFromClipvoard();
            } else if (result.state === "prompt") {
              logger.log("no permission");
            }
            logger.log("not expected value" + result.state);
            // Don't do anything if the permission was denied.
          })
          .error((er: any) => {
            logger.error(er);
          });
      } catch (er) {
        logger.error(er);
        showToast({
          text: state.translate("ShowRoomSubmittalsBackground.clipboardNotSupported", "no clipboard supported"),
          isError: true,
        });
        return;
      }
    }
    /**
     * The function `loadPictureFromClipboard` sets the selected image name, retrieves an image from the
     * clipboard as a base64 string, and handles different scenarios based on the image content.
     * @returns If the condition `!showClipboardRow` is met, the function will return early and nothing
     * will be executed beyond that point. If the condition is not met, the function will check if the
     * `img` variable starts with "data:image". If it does, it will set the image on the clipboard, update
     * the selected image name, set the clipboard image URL, and update the selected image URL
     */
    async function loadPictureFromClipvoard() {
      setSelectedImageName(name);
      const img = await imageFromClipboardAsBase64();
      if (!showClipboardRow) {
        return;
      }
      if (img?.substring(0, 10) == "data:image") {
        setImageOnClipboard(img);
        setSelectedImageName(name);
        clipboardImageUrl.current = img;
        setSelectedImageURL(img);
      } else if (img) {
        showToast({
          text: state.translate("step3.noPictureInClipboard", "Die Zwischenablage enthät kein Bild"),
        });
      }
    }
    let lableClassName = "backgroundRoomSubmittals"; // no itx
    if (imageOnClipboard != emptyImage) {
      lableClassName += " notEmpty"; // no itx
    }

    return (
      <ListRow
        name={name}
        imgSrc={imageOnClipboard}
        description={getDescription()}
        additionalElement={
          <div
            className={lableClassName}
            style={{
              borderRadius: "2px",
              height: "68px",
              width: `100px`,
              float: "left",
              position: "absolute",
              top: "0px",
              left: "0px",
              textAlign: "center",
              color: "black", // imageUploaded == emptyImage ? "black" : "grey",
              cursor: "pointer",
            }}
            onClick={async () => {
              //getPermissionAccessClipboard();
              loadPictureFromClipvoard();
            }}
          >
            <i className="las la-sync" style={{ fontSize: "32px", paddingTop: "6px" }} />
            <br />
            {state.translate("ShowRoomSubmittalsBackground.reload", "reload")}
          </div>
        }
      />
    );
  }

  /**
   * The `ListRowUpload` function in TypeScript React component handles uploading and displaying images
   * with additional functionality for file selection.
   * @param props - The `ListRowUpload` component takes a single prop `imgSrc`, which is a string
   * representing the source of an image. This prop is used to display the image in the component.
   */
  function ListRowUpload(props: { imgSrc: string }) {
    const name = "fromUpload"; // no itx
    const [imageDownloaded, setImageDownloaded] = useState(props.imgSrc);

    // for FileUpload
    const handleFileUpload = async (event: any) => {
      state.backgroundImmageHandler.downloadImage(event, page, (img: string) => {
        setImageDownloaded(img);
        uploadImageUrl.current = img;
        setSelectedImageURL(img);
      });
    };

    function getDescription(): string {
      if (imageDownloaded == emptyImage) {
        return state.translate(
          "ShowRoomSubmittalsBackground.loadPictureFromLocalServer",
          "load an png svg... from local server",
        );
      } else {
        return state.translate("ShowRoomSubmittalsBackground.pictureFromLocalServer", "picture from local server");
      }
    }

    let lableClassName = "backgroundRoomSubmittals"; // no itx
    if (imageDownloaded != emptyImage) {
      lableClassName += " notEmpty"; // no itx
    }

    return (
      <ListRow
        name={name}
        description={getDescription()}
        imgSrc={imageDownloaded}
        // onClick={() => {}}
        additionalElement={
          <label
            className={lableClassName}
            // style={{
            //   borderRadius: "2px",
            //   height: "68px",
            //   width: `100px`,
            //   float: "left",
            //   position: "absolute",
            //   top: "0px",
            //   left: "0px",
            //   textAlign: "center",
            //   color: "black", // imageUploaded == emptyImage ? "black" : "grey",
            //   cursor: "pointer",
            // }}
          >
            <input
              style={{ display: "none" }}
              id="file"
              name="file"
              type="file"
              //accept="image/*"
              onClick={(event) => {
                event.stopPropagation;
              }}
              onChange={(event) => {
                setSelectedImageName(name);
                handleFileUpload(event);
              }}
            />
            <i className="las la-upload" style={{ fontSize: "32px", paddingTop: "6px" }} />
            <br />
            {state.translate("ShowRoomSubmittalsBackground.selectFile", "Datei auswählen")}
          </label>
        }
      />
    );
  }
  var imageStyle: any = {
    height: "auto",
    width: `100px`,
    objectFit: "contain",
    marginRight: "50px",
  };

  // define the list of Background Images
  let demoRoomBackgroundList = new Array<{
    image: string;
    description: string;
  }>();

  demoRoomBackgroundList = [
    {
      image: "Frame 1.png", // no itx
      description: state.translate("ShowRoomSubmittalsBackground.descriptionFrame1", "Links Fenster, Rechts Türe"),
    },
    {
      image: "Frame 2.png", // no itx
      description: state.translate(
        "ShowRoomSubmittalsBackground.descriptionFrame2",
        "Links Fenster, Unten Flügeltüre, Rechts Türe ",
      ),
    },
    {
      image: "Frame 3.png", // no itx
      description: state.translate("ShowRoomSubmittalsBackground.descriptionFrame3", "Links Türe, Rechts Fenster"),
    },
    {
      image: "Frame 4.png", // no itx
      description: state.translate(
        "ShowRoomSubmittalsBackground.descriptionFrame4",
        "Links Fenster, Unten zwei Fenster, Rechts Türe",
      ),
    },
    {
      image: "Frame 5.png", // no itx
      description: state.translate(
        "ShowRoomSubmittalsBackground.descriptionFrame5",
        "Unten Türe Trennwand und Rechts Fenster",
      ),
    },
    {
      image: "Frame 6.png", // no itx
      description: state.translate(
        "ShowRoomSubmittalsBackground.descriptionFrame6",
        "Links zwei Fenster , Unten großes Fesnter, Oben Türe, Rechts Flügeltüre",
      ),
    },
    // {
    //   image: "room_window_corner.png",
    //   description: state.translate(
    //     "ShowRoomSubmittalsBackground.descriptionRoomWindowCorner",
    //     "",
    //   ),
    // },
    {
      image: "caro.png", // no itx
      description: state.translate("ShowRoomSubmittalsBackground.descriptionCaro", "Karierter Hintergrund"),
    },
  ];
  // show them in Dialog
  const imageList = new Array<any>();
  // for upload

  imageList.push(<ListRowUpload imgSrc={uploadImageUrl.current} />);

  // for clipboards
  if (showClipboardRow && (!isApple || !isFirefox)) {
    imageList.push(<ListRowClipboard imgSrc={clipboardImageUrl.current} />);
  }

  demoRoomBackgroundList.forEach(({ image, description }) => {
    imageList.push(
      <ListRow
        key={image}
        name={image}
        description={description}
        imgSrc={state.hostBaseUrl + "images/roomDemo/" + image}
      />,
    );
  });

  return (
    <BaseDialog
      title={state.translate(
        "ShowRoomSubmittalsBackground.selectSubmittalDescription",
        "Hier können sie einen Zeichnung auswählen und diese als Hintergundbild Ihres Raumes verwenden.",
      )}
      onOk={() => {
        if (selectedImageURL != "" && selectedImageURL != emptyImage) {
          onChangeBackgroundImgUrl(selectedImageURL);
          //page.backgroundImgUrl = selectedImageURL;

          page.raiseOnChange("backgroundImgUrl");
          if (onChange) {
            onChange();
          }
          if (fromRoomCreation?.parent) {
            state.dialogManager.openDialog(
              <RoomDialog
                parent={fromRoomCreation?.parent}
                fromSubmittals={{
                  background: selectedImageURL,
                  tag: fromRoomCreation.tag,
                  name: fromRoomCreation.name,
                  cabinetId: fromRoomCreation.cabinetId,
                }}
              />,
            );
            return false;
          }
          return true;
        }
        return false;
      }}
      noOkButton={selectedImageURL == "" ? true : selectedImageURL == emptyImage ? true : false}
    >
      <div
        style={{
          // maxWidth: "100%",
          height: "400px",
          width: "calc(100% + 40px)",
          margin: "-20px",
        }}
      >
        <div
          style={{
            height: "400px",
            width: "100%",
            marginBottom: "10px",
            padding: "10px",
            overflow: "auto",
          }}
        >
          <table>
            <tbody>{imageList}</tbody>
          </table>
        </div>
      </div>
    </BaseDialog>
  );
}

export function ShowRoomSubmittalsBackgroundInstantSet(props: {
  page: Page;
  onChange?: () => void;
  fromRoomCreation?: { parent: Floor; tag: string; name: string; cabinetId: string };
}) {
  const { page, onChange, fromRoomCreation } = props;
  const isApple = Browser.getBrowser().includes("Safari");
  const isFirefox = Browser.getBrowser().includes("Firefox");
  const emptyImage =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQgAAAEGCAYAAACZ/AuPAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAMTSURBVHhe7dQxAYBAEMCwA/9azwIsbE8dJEsd9NrdZwB+3F8BDgYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggGQQQDIIIBkEkAwCSAYBJIMAkkEAySCAZBBAMgggGQSQDAJIBgEkgwCSQQDJIIBkEEAyCCAZBJAMAkgGASSDAJJBAMkggDDzAjBpBdXtsdrzAAAAAElFTkSuQmCC"; // no itx

  // define the list of Background Images
  let demoRoomBackgroundList = new Array<{
    image: string;
    description: string;
  }>();

  demoRoomBackgroundList = [
    {
      image: "Frame 1.png", // no itx
      description: state.translate("ShowRoomSubmittalsBackground.descriptionFrame1", "Links Fenster, Rechts Türe"),
    },
    {
      image: "Frame 2.png", // no itx
      description: state.translate(
        "ShowRoomSubmittalsBackground.descriptionFrame2",
        "Links Fenster, Unten Flügeltüre, Rechts Türe ",
      ),
    },
    {
      image: "Frame 3.png", // no itx
      description: state.translate("ShowRoomSubmittalsBackground.descriptionFrame3", "Links Türe, Rechts Fenster"),
    },
    {
      image: "Frame 4.png", // no itx
      description: state.translate(
        "ShowRoomSubmittalsBackground.descriptionFrame4",
        "Links Fenster, Unten zwei Fenster, Rechts Türe",
      ),
    },
    {
      image: "Frame 5.png", // no itx
      description: state.translate(
        "ShowRoomSubmittalsBackground.descriptionFrame5",
        "Unten Türe Trennwand und Rechts Fenster",
      ),
    },
    {
      image: "Frame 6.png", // no itx
      description: state.translate(
        "ShowRoomSubmittalsBackground.descriptionFrame6",
        "Links zwei Fenster , Unten großes Fesnter, Oben Türe, Rechts Flügeltüre",
      ),
    },
    {
      image: "caro.png", // no itx
      description: state.translate("ShowRoomSubmittalsBackground.descriptionCaro", "Karierter Hintergrund"),
    },
  ];

  function closeDialog(img: string) {
    if (fromRoomCreation?.parent) {
      state.dialogManager.openDialog(
        <RoomDialog
          parent={fromRoomCreation?.parent}
          fromSubmittals={{
            background: img || emptyImage,
            tag: fromRoomCreation.tag,
            name: fromRoomCreation.name,
            cabinetId: fromRoomCreation.cabinetId,
          }}
        />,
      );
      return;
    } else {
      state.dialogManager.closeDialog();
    }
  }

  function ListRow(props: {
    name: string;
    description: string;
    imgSrc: string;
    additionalElement?: any;
    onClick?: (ev: any) => boolean;
    input?: any;
  }) {
    const { name, description, imgSrc, additionalElement, onClick, input } = props;

    const element = (
      <div style={{ cursor: "pointer", position: "relative" }}>
        <img
          className="backgroundPicture"
          src={imgSrc}
          style={{
            border: "1px solid lightgrey",
            borderRadius: "2px",
            height: "68px",
            width: "100px",
            backgroundColor: "var(--panel-background)",
            cursor: "pointer",
          }}
          id={name}
        />
        {additionalElement}
      </div>
    );

    return (
      <tr
        key={`image_${imgSrc}`}
        id={`backgroundPicture_${name}`}
        style={{
          borderRadius: "5px",
          cursor: "pointer",
        }}
        onClick={async (ev) => {
          if (onClick) {
            onClick(ev);
          } else {
            const image = document.getElementById(name) as HTMLImageElement;

            if (image) {
              // transform img element to canvas an convert the cavas to an image element
              const toDataURL = () => {
                const canvas = document.createElement("canvas");

                canvas.width = image.naturalWidth; // real immage width
                canvas.height = image.naturalHeight; // real immage heght
                const canvas2D = canvas.getContext("2d");
                if (canvas2D) {
                  canvas2D.drawImage(image, 0, 0);

                  const dataURL = canvas.toDataURL();
                  return dataURL;
                }
              };
              const imageUrl = toDataURL();
              if (imageUrl && imageUrl != emptyImage) {
                //const result = await page.resizeImageAfterLoad(imageUrl);
                // if (result != "" && result != emptyImage) {
                onChangeBackgroundImgUrl(imageUrl as string);

                page.raiseOnChange("backgroundImgUrl");
                if (onChange) {
                  onChange();
                }
                closeDialog(imageUrl as string);
                return;
                // }
              }
            }
          }

          return false;
        }}
      >
        <td
          style={{
            paddingTop: "10px",
            paddingBottom: "10px",
            width: "60px",
          }}
        >
          {element}
        </td>
        <td
          id={`backgroundPicture_${name}_click`}
          style={{
            verticalAlign: "middle",
          }}
        >
          <label className={""}>
            {input}
            <div style={{ whiteSpace: "normal" }}> {description}</div>
          </label>
        </td>
      </tr>
    );
  }

  function ListRowUpload(props: {}) {
    const name = "fromUpload"; // no itx

    // for FileUpload
    async function handleFileUpload(event: any) {
      state.backgroundImmageHandler.downloadImage(event, page, (img: string) => {
        onChangeBackgroundImgUrl(img);
        closeDialog(img);
      });
    }

    function getDescription(): string {
      return state.translate(
        "ShowRoomSubmittalsBackground.loadPictureFromLocalServer",
        "load an png svg... from local server",
      );
    }

    let lableClassName = "backgroundRoomSubmittals"; // no itx

    return (
      <ListRow
        name={name}
        description={getDescription()}
        imgSrc={emptyImage}
        additionalElement={
          <label className={lableClassName}>
            <input
              style={{ display: "none" }}
              id="file"
              name="file"
              type="file"
              onClick={(event) => {
                event.stopPropagation;
              }}
              onChange={(event) => {
                handleFileUpload(event);
              }}
            />
            <i className="las la-upload" style={{ fontSize: "32px", paddingTop: "6px" }} />
            <br />
            {state.translate("ShowRoomSubmittalsBackground.selectFile", "Datei auswählen")}
          </label>
        }
        onClick={(ev) => {
          ev.stopPropagation();
          return true;
        }}
        input={
          <input
            style={{ display: "none" }}
            id="file"
            name="file"
            type="file"
            onClick={(event) => {
              event.stopPropagation;
            }}
            onChange={(event) => {
              handleFileUpload(event);
            }}
          />
        }
      />
    );
  }
  function ListRowClipboard(props: {}) {
    const name = "clipboard"; // no itx

    //for paste from Clipboard
    async function imageFromClipboardAsBase64(): Promise<string | undefined> {
      try {
        logger.log("onPasteBackground");
        const clipboardItems = await navigator.clipboard.read();
        if (!clipboardItems || clipboardItems.length == 0) {
          return undefined;
        }

        const firstItem = clipboardItems[0];

        for (const type of firstItem.types) {
          if (type == "image/png" || type == "image/jpg" || type == "image/jpeg") {
            const blob = await clipboardItems[0].getType(type);

            const base64data = await readBlobAsync(blob);
            return base64data;
          } else {
            return type;
          }
        }
        return undefined;
      } catch (ex) {
        logger.error(ex);
        showToast({
          text: state.translate("ShowRoomSubmittalsBackground.clipboardNotSupported", "no clipboard supported"),
          isError: true,
        });
        return;
      }
    }

    function readBlobAsync(blob: Blob): Promise<string> {
      return new Promise((resolve, reject) => {
        let reader = new FileReader();

        reader.readAsDataURL(blob);
        reader.onloadend = function () {
          var base64data = reader.result;
          if (typeof base64data == "string") {
            // logger.log(base64data);
            resolve(base64data);
          }
        };

        reader.onerror = reject;
      });
    }

    function getDescription(): string {
      return state.translate("ShowRoomSubmittalsBackground.noPictureInClipboard", "no picture in clipboard");
    }

    function getPermissionAccessClipboard() {
      try {
        (navigator.permissions as any)
          .query({ name: "clipboard-read" }) // no itx
          .then((result: any) => {
            if (result.state === "granted") {
              loadPictureFromClipboard();
            } else if (result.state === "prompt") {
              logger.log("no permission");
            }
            logger.log("not expected value" + result.state);
            // Don't do anything if the permission was denied.
          })
          .error((er: any) => {
            logger.error(er);
          });
      } catch (er) {
        logger.error(er);
        showToast({
          text: state.translate("ShowRoomSubmittalsBackground.clipboardNotSupported", "no clipboard supported"),
          isError: true,
        });
        return;
      }
    }

    async function loadPictureFromClipboard() {
      const img = await imageFromClipboardAsBase64();
      if (img?.substring(0, 10) == "data:image") {
        onChangeBackgroundImgUrl(img);
        closeDialog(img);
      } else if (img) {
        showToast({
          text: state.translate("step3.noPictureInClipboard", "Die Zwischenablage enthät kein Bild"),
        });
      }
    }
    let lableClassName = "backgroundRoomSubmittals"; // no itx

    return (
      <ListRow
        name={name}
        imgSrc={emptyImage}
        description={getDescription()}
        additionalElement={
          <div
            className={lableClassName}
            style={{
              borderRadius: "2px",
              height: "68px",
              width: "100px",
              float: "left",
              position: "absolute",
              top: "0px",
              left: "0px",
              textAlign: "center",
              color: "black", // imageUploaded == emptyImage ? "black" : "grey",
              cursor: "pointer",
            }}
          >
            <i className="las la-paste" style={{ fontSize: "32px", paddingTop: "6px" }} />
            <br />
            {state.translate("ShowRoomSubmittalsBackground.reload", "reload")}
          </div>
        }
        onClick={(ev) => {
          ev.stopPropagation();
          loadPictureFromClipboard();
          return true;
        }}
      />
    );
  }
  // change background Image from Page
  function onChangeBackgroundImgUrl(backgroundImgUrl: string) {
    async function getImagevirtualElement(url: string) {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    }
    const mainDrawingArea = document.getElementById("mainDrawingArea");
    let imageHeight = 0;
    let imageWidth = 0;

    let areaHeight = 21 * 33;
    let areaWidth = 29.7 * 33;
    if (mainDrawingArea) {
      areaHeight = mainDrawingArea.offsetHeight;
      areaWidth = mainDrawingArea.offsetWidth;
    }
    page.resizeImageAfterLoad(backgroundImgUrl).then((result) => {
      getImagevirtualElement(result as string).then((img) => {
        imageWidth = img.naturalWidth;
        imageHeight = img.naturalHeight;
        const newPosY = (areaHeight - imageHeight) / 2;
        const newPosX = (areaWidth - imageWidth) / 2;
        const newBackgroundImagePosition = new BackgroundImagePosition();
        newBackgroundImagePosition.posY = newPosY;
        newBackgroundImagePosition.posX = newPosX;
        newBackgroundImagePosition.scale = state.settings.defaultBackgroundPictureElements.scale;
        newBackgroundImagePosition.fade = state.settings.defaultBackgroundPictureElements.fade;
        if (!fromRoomCreation?.parent) {
        }
        page.backgroundImgUrl = result as string;
        page.backgroundImagePosition = newBackgroundImagePosition;
        page.raiseOnChange("backgroundImgUrl");
        page.raiseOnChange("backgroundImagePosition");
      });
    });
  }
  // show them in Dialog
  const imageList = new Array<any>();

  imageList.push(<ListRowUpload />);

  // for clipboards
  if (!isApple || !isFirefox) {
    imageList.push(<ListRowClipboard />);
  }

  demoRoomBackgroundList.forEach(({ image, description }) => {
    imageList.push(
      <ListRow
        key={image}
        name={image}
        description={description}
        imgSrc={state.hostBaseUrl + "images/roomDemo/" + image}
      />,
    );
  });
  return (
    <BaseDialog
      title={state.translate(
        "ShowRoomSubmittalsBackground.selectSubmittalDescription",
        "Hier können sie einen Zeichnung auswählen und diese als Hintergundbild Ihres Raumes verwenden.",
      )}
      noOkButton={true}
    >
      <div
        style={{
          // maxWidth: "100%",
          height: "400px",
          width: "calc(100% + 40px)",
          margin: "-20px",
        }}
      >
        <div
          style={{
            height: "400px",
            width: "100%",
            marginBottom: "10px",
            padding: "10px",
            overflow: "auto",
          }}
        >
          <table>
            <tbody>{imageList}</tbody>
          </table>
        </div>
      </div>
    </BaseDialog>
  );
}
